/**
 * @module "form"
 * @description Form handles all operations related to manipulating template objects
 * within a template. Template is a model, which represents all template objects inside a form
 * Template object is a model, which represents a single element on the form template
 * Template object type is a model, which represents the type of the object (i.e Group, Input etc)
 * @property {string} contentChannelName - Backbone.Radio channel for registering and getting content views
 * @property {string} modelRelationshipChannelName - Backbone.Radio channel for registering and getting model relationships
 * @property {object} templateModel -Backbone.Model for the root template object for the entire form.
 * @property {object} templateObjectIdMap - Hash of template objects with the template object's GUID as the reference key
 * @property {object} conditionsByTargetIdMap - Hash of template object conditions with the target object's GUID as reference key
 * @property {object} actions - Backbone.Collection
 * @property {object} operations - Backbone.Collection
 * @property {object} templateObjectTypeGroupCollection - Backbone.Collection
 * @property {object} confirmationModal - UIKit.Modal
 * @property {string} compareHiddenMessage
 *
 * @todo better understanding and descriptions for actions, operations, confirmationModal and templateObjectTypeGroupCollection

 */
define('formkit/form',['require','exports','module','underscore','jquery','backbone','backbone.radio','uuid','./model/answer.model','./model/template.model','./model/template.object.model','./model/template.object.type.model','./form.object.util','./form.object.status.util','./rule.util','./validation.util','uikit/uikit','bluebird','./view/form.object/content/base.content.view','./view/form.object/content/input.content.view','./view/form.object/content/checkbox.content.view','./view/form.object/content/radio.content.view','./view/form.object/content/dropdown.content.view','./view/form.object/content/information.content.view','./view/form.object/content/date.input.content.view','./view/form.object/content/numeric.input.content.view','./view/form.object/content/currency.input.content.view','./view/form.object/content/table.content.view','./view/form.object/content/date.time.content.view'],function(require, exports, module) {
  //prettier-ignore
  var _                       = require('underscore'),
      jQuery                  = require('jquery'),
      Backbone                = require('backbone'),
      Radio                   = require('backbone.radio'),
      UUID                    = require('uuid'),
      AnswerModel             = require('./model/answer.model'),
      TemplateModel           = require('./model/template.model'),
      TemplateObjectModel     = require('./model/template.object.model'),
      TemplateObjectTypeModel = require('./model/template.object.type.model'),
      FormObjectUtil          = require('./form.object.util'),
      FormObjectStatusUtil    = require('./form.object.status.util'),
      RuleUtil                = require('./rule.util'),
      ValidationUtil          = require('./validation.util'),
      UIKit                   = require('uikit/uikit'),
      Promise                 = require('bluebird'),

      // content views
      BaseContentView          = require('./view/form.object/content/base.content.view'),
      InputContentView         = require('./view/form.object/content/input.content.view'),
      CheckboxContentView      = require('./view/form.object/content/checkbox.content.view'),
      RadioContentView         = require('./view/form.object/content/radio.content.view'),
      DropdownContentView      = require('./view/form.object/content/dropdown.content.view'),
      InformationContentView   = require('./view/form.object/content/information.content.view'),
      DateInputContentView     = require('./view/form.object/content/date.input.content.view'),
      NumericInputContentView  = require('./view/form.object/content/numeric.input.content.view'),
      CurrencyInputContentView = require('./view/form.object/content/currency.input.content.view'),
      TableContentView         = require('./view/form.object/content/table.content.view'),
      DateTimeContentView      = require('./view/form.object/content/date.time.content.view');

  /**
   * Creates a new form
   * @class Form
   * @classdesc A form is an object which manages a customizable mechanism for receiving, storing, and modifying data from a user.
   *
   */
  var Form = Mn.Object.extend(
    {
      contentChannelName: 'formkit.formobject.content',
      modelRelationshipChannelName: 'formkit.model.relationship',

      /**
       *  Exposes the various FormKit utilities immediately to the form object
       * @todo Find instances where we unnecessarily require in utility libraries when the form object is available.
       */
      util: _.extend({}, FormObjectUtil, RuleUtil, ValidationUtil, FormObjectStatusUtil),

      /**
       * templateObjectDefaults - Generates a "defaults" object to initialize new templateObjectModels
       *
       * @returns {object}  An object of default key/value pairs for a new template model
       */
      templateObjectDefaults: function() {
        return {
          id: UUID.generate(),
        };
      },

      actions: null,

      operations: null,

      templateObjectIdMap: null,

      conditionsByTargetIdMap: null,

      confirmationModal: null,

      localConfig: null,

      compareHiddenMessage: 'No question available.',

      /**
       * @method initialize - Supers Mn.Object and initializes form objects, properties, and listeners
       * @constructs
       * @augments external:Marionette.Object
       * @listens {module:form~event:detach:templateObject}
       * @listens {module:form~event:remove:templateObject}
       * @listens {module:form~event:add:templateObject}
       * @listens {module:form~event:remove:condition}
       * @listens {module:form~event:add:rule}
       * @fires Form#change:activeSectionModel
       * @fires Form#conditionsMap:add
       * @fires Form#conditionsMap:remove
       * @returns {undefined}
       *
       * @todo consolidate remove:templateObject listeners to one function
       */
      initialize: function() {
        Mn.Object.prototype.initialize.apply(this, arguments);

        this.mergeOptions(this.options, [
          'templateModel',
          'templateObjectTypeCollection',
          'confirmationModal',
          'localConfig',
          'compareHiddenMessage',
        ]);

        if (!this.templateModel) {
          this.templateModel = new TemplateModel();
        }

        this.templateObjectIdMap = {};
        this.conditionsByTargetIdMap = {};

        this.actions = new Backbone.Collection();
        this.operations = new Backbone.Collection();
        this.templateObjectTypeGroupCollection = new Backbone.Collection([], {
          comparator: 'sortIndex',
        });

        // listen to template sync and initialize active section
        var resourceSyncCallback = _.after(
          this.templateObjectTypeCollection ? 2 : 1,
          _.bind(this.initializeActiveSection, this)
        );
        this.listenToOnce(this.templateModel, 'sync change:id', resourceSyncCallback);
        this.listenToOnce(this.templateObjectTypeCollection, 'sync change:id', resourceSyncCallback);

        this.on('detach:templateObject', function(templateObject) {
          if (templateObject == this.getActiveSectionModel()) {
            this.initializeActiveSection();
          }
        });

        this.on('remove:templateObject', function(templateObject) {
          if (templateObject == this.getActiveSectionModel()) {
            _.defer(
              _.bind(function() {
                if (FormObjectUtil.indexOfChild(this.templateModel, templateObject) < 0) {
                  this.initializeActiveSection();
                }
              }, this)
            );
          }
        });

        this.listenTo(this.templateObjectTypeCollection, 'sync', function() {
          this.refreshTemplateObjectTypeGroupCollection();
        });

        this.setupDefaultActions();
        this.setupDefaultOperations();

        this.addAllDescendentsToTemplateObjectMap(this.templateModel);
        this.addAllDescendentsToConditionsByTargetIdMap(this.templateModel);
        this.bindTemplateObjectEventsToAllDescendents(this.templateModel);

        this.on('remove:templateObject', function(templateObjectModel) {
          this.unbindTemplateObjectEventsFromAllDescendents(templateObjectModel);
          this.removeAllDescendentsFromTemplateObjectMap(templateObjectModel);
          this.removeAllDescendentsFromConditionsByTargetIdMap(templateObjectModel);
        });
        this.on('add:templateObject', function(templateObjectModel) {
          this.bindTemplateObjectEventsToAllDescendents(templateObjectModel);
          this.addAllDescendentsToTemplateObjectMap(templateObjectModel);
          this.addAllDescendentsToConditionsByTargetIdMap(templateObjectModel);
        });

        this.on('remove:condition', function(condition, rule, templateObject) {
          // check if condition is a the only one left and remove the rule then
          if (!this.util.ruleHasConditions(rule)) {
            this.util.removeRule(rule);
          }
        });

        this.on('add:rule', function(rule) {
          if (!this.util.ruleHasConditions(rule)) {
            this.util.insertCondition(rule, {});
          }
        });
      },

      /**
       * enableTemplateObjectTypeCounters
       * @description Sets listeners to update the template object type count
       * @listens {external:backbone.Collection~event:sync}
       * @listens {module:"model/template.model"~event:sync}
       * @listens {module:"form"~event:add:templateObject}
       * @listens {module:"form"~event:remove:templateObject}
       * @returns {undefined}
       *
       * @todo The external reference to Backbone.Collection in the listener annotation is most likeyly incorrect
       */
      enableTemplateObjectTypeCounters: function() {
        this.listenToOnce(this.templateObjectTypeCollection, 'sync', this.updateTemplateObjectTypeCounters)
          .listenTo(this.templateModel, 'sync', this.updateTemplateObjectTypeCounters)
          .on('add:templateObject remove:templateObject', this.updateTemplateObjectTypeCounters);

        this.updateTemplateObjectTypeCounters();
      },

      /**
       * enableConditionMapTracking
       * @description Sets listeners to update the conditionsByTargetIdMap
       *
       * @listens {module:'form'~event:add:condition}
       * @listens {module:'form'~event:modify:condition:targetId}
       * @listens {module:'form'~event:remove:condition}
       * @listens {module:'form'~event:add:rule}
       * @listens {module:'form'~event:remove:rule}
       * @returns {undefined}
       */
      enableConditionMapTracking: function() {
        this.on('add:condition', function(condition) {
          this.addConditionToConditionsMap(condition);
        })
          .on('modify:condition:targetId', function(condition) {
            this.modifyConditionInConditionsMap(condition);
          })
          .on('remove:condition', function(condition, rule, templateObject) {
            this.removeConditionFromConditionsMap(condition);
          });

        this.on('add:rule', function(rule) {
          this.util.eachCondition(
            rule,
            function(condition) {
              this.addConditionToConditionsMap(condition);
            },
            this
          );
        }).on('remove:rule', function(rule) {
          this.util.eachCondition(
            rule,
            function(condition) {
              this.removeConditionFromConditionsMap(condition);
            },
            this
          );
        });
      },

      /**
       * getConfirmationModal
       * @description Returns the current value of this.confirmationModal or
       * creates a new UIKit.Modal view, using the passed options
       *
       * @param  {object} options
       * @returns {object} - UIKit.Modal
       */
      getConfirmationModal: function(options) {
        if (this.confirmationModal) {
          return new this.confirmationModal(options);
        } else {
          return new UIKit.Modal(options);
        }
      },

      /**
       * initializeActiveSection
       * @description Determines and sets the active section of a form
       *
       * @returns {undefined}
       */
      initializeActiveSection: function() {
        var activeSectionModel = this.templateModel.get('children').find(function(child) {
          return !(child.get('displayed') === false);
        });
        this.setActiveSectionModel(activeSectionModel);
      },

      /**
       * setActiveSectionModel
       * @description Invokes initialization the active section of a template
       *
       * @param  {object} newActiveSectionModel Backbone.Model
       * @returns {undefined}
       */
      setActiveSectionModel: function(newActiveSectionModel) {
        if (this.activeSectionModel != newActiveSectionModel) {
          var oldActiveSectionModel = this.activeSectionModel;
          this.activeSectionModel = newActiveSectionModel;

          this.trigger('change:activeSectionModel', newActiveSectionModel, oldActiveSectionModel);

          this.setActiveSectionIndex(this.templateModel.get('children').indexOf(newActiveSectionModel));
        }
      },

      /**
       * setActiveSectionIndex
       * @description updates the value of this.activeSectionIndex with the index
       * of current active section
       *
       * @param  {number} activeSectionIndex
       * @returns {undefined}
       */
      setActiveSectionIndex: function(activeSectionIndex) {
        var oldActiveSectionIndex = this.activeSectionIndex;
        if (this.activeSectionIndex != activeSectionIndex) {
          this.activeSectionIndex = activeSectionIndex;
          this.trigger('change:activeSectionIndex', activeSectionIndex, oldActiveSectionIndex);

          this.setActiveSectionModel(this.templateModel.get('children').at(activeSectionIndex));
        }
      },

      /**
       * getActiveSectionModel
       * @description returns the template object model of the currently active section
       *
       * @returns {object}  Backbone.Model
       */
      getActiveSectionModel: function() {
        return this.activeSectionModel;
      },

      /**
       * getActiveSectionIndex - Relative index of the currently active section template object
       *
       * @returns {number}  Current value of this.activeSectionIndex
       */
      getActiveSectionIndex: function() {
        return this.activeSectionIndex;
      },

      /**
       * updateTemplateObjectTypeCounters
       * @description Creates and updates an object at this.templateObjectTypeCounters
       * enummerating the numbers of the different template object types in a template
       *
       * @returns {undefined}
       */
      updateTemplateObjectTypeCounters: function() {
        this.templateObjectTypeCounters = {};

        // Keep a counter for the number of template objects for each type
        FormObjectUtil.eachDescendent(
          this.templateModel,
          function(templateObject) {
            var currentTypeName = this.getTemplateObjectTypeName(templateObject),
              counter = this.templateObjectTypeCounters[currentTypeName] || 0;

            this.templateObjectTypeCounters[currentTypeName] = counter + 1;
          },
          this
        );
      },

      /**
       * maxMultiplicityReached
       * @description Returns true if a template object type has reached or exceeded
       * its maxMultiplicity, otherwise returns false
       *
       * @param  {object} templateObjectTypeModel
       * @returns {boolean}
       */
      maxMultiplicityReached: function(templateObjectTypeModel) {
        var typeName = this.getTemplateObjectTypeNameFromTypeModel(templateObjectTypeModel);
        var typeCounter = (this.templateObjectTypeCounters || {})[typeName] || 0;
        var maxMultiplicity;

        if (templateObjectTypeModel instanceof Backbone.Model) {
          maxMultiplicity = templateObjectTypeModel.get('maxMultiplicity');
        } else {
          maxMultiplicity = _.result(templateObjectTypeModel, 'maxMultiplicity');
        }

        if (maxMultiplicity == null) {
          return false;
        }
        return typeCounter >= maxMultiplicity;
      },

      /**
       * getTemplateModel
       * @description Returns the template model for the form
       *
       * @returns {object}  the current value of this.templateModel
       */
      getTemplateModel: function() {
        return this.templateModel;
      },

      /**
       * getTemplateObjectId
       *
       * @param  {object} model description
       * @returns {string} the model's ID
       *
       * @todo Figure out in what situation we would get a model that's not an object.
       */
      getTemplateObjectId: function(model) {
        return _.isObject(model) ? model.id : model;
      },

      /**
       * getTemplateObjectTypeCollection
       *
       * @returns {object}  the current value of this.templateObjectTypeCollection
       */
      getTemplateObjectTypeCollection: function() {
        return this.templateObjectTypeCollection;
      },

      /**
       * getTemplateObjectTypeGroupCollection
       *
       * @returns {object}  the curernt value of templateObjectTypeGroupCollection
       */
      getTemplateObjectTypeGroupCollection: function() {
        return this.templateObjectTypeGroupCollection;
      },

      /**
       * refreshTemplateObjectTypeGroupCollection
       * @description sets the order in which groups are displayed in the submission template builder
       *
       * @returns {undefined}
       */
      refreshTemplateObjectTypeGroupCollection: function() {
        // NOTE: As more products release product-specific template object types, add them to the top of groupIndexes
        // with a value of 0 in alphabetical order.
        var groupIndexes = {
          "SP Objects": 0,
          Objects: 1,
          Finders: 2,
        };
        var groupCollectionMapByName = {};
        var groupArray = [];
        var templateObjectTypeCollection = this.getTemplateObjectTypeCollection() || new TemplateObjectTypeModel.Collection();

        templateObjectTypeCollection.each(function(model) {
          var groupName = model.get('grouping');
          var groupSortIndex = groupIndexes[groupName] || 0;

          if (!!groupName) {
            if (!groupCollectionMapByName[groupName]) {
              groupCollectionMapByName[groupName] = new Backbone.Collection();
              groupArray.push({
                sortIndex: groupSortIndex,
                name: groupName,
                collection: groupCollectionMapByName[groupName],
              });
            }
            groupCollectionMapByName[groupName].push(model);
          }
        }, this);

        groupCollectionMapByName = null;
        this.templateObjectTypeGroupCollection.reset(groupArray);
      },

      /**
       * getTemplateObjectType
       *
       * @param  {object} model a templateObjectModel
       * @returns {object} the templateObjectTypeModel of the templateObjectModel
       */
      getTemplateObjectType: function(model) {
        if (model instanceof Backbone.Model) {
          return model.get('type');
        }
        return _.result(model, 'type');
      },

      /**
       * getTemplateObjectTypeNameFromTypeModel
       *
       * @param  {object} typeModel a templateObjectTypeModel
       * @returns {string} the name of the template object type
       */
      getTemplateObjectTypeNameFromTypeModel: function(typeModel) {
        if (typeModel instanceof Backbone.Model) {
          return typeModel.get('description');
        } else if (typeModel && typeModel.description) {
          return typeModel.description;
        }
        return typeModel;
      },

      /**
       * getTemplateObjectTypeName
       *
       * @param  {object} model a template object model
       * @returns {string} the name of the template object type
       */
      getTemplateObjectTypeName: function(model) {
        var typeModel = this.getTemplateObjectType(model);
        return this.getTemplateObjectTypeNameFromTypeModel(typeModel);
      },

      /**
       * findTemplateObjectTypeByName
       *
       * @param  {string} name name of a template object type, e.g. 'Section', 'Dropdown', etc.
       * @returns {object|null} first templateObjectTypeModel with the given name if found, otherwise null
       */
      findTemplateObjectTypeByName: function(name) {
        var collection = this.getTemplateObjectTypeCollection(),
          models = [],
          typeName = '',
          lowerName = String(name).toLowerCase();

        if (collection instanceof Backbone.Collection) {
          models = collection.models;
        }

        for (var i = 0, len = models.length; i < len; ++i) {
          typeName = this.getTemplateObjectTypeNameFromTypeModel(models[i]);
          if (String(typeName).toLowerCase() == lowerName) {
            return models[i];
          }
        }
        return null;
      },

      /**
       * getTemplateObjectById
       * @description will return a hash of template objects with the template object's GUID
       * as the reference key
       *
       * @param  {object} model templateObjectModel
       * @returns {object} templateObjectModel
       *
       * @todo Wait? What??? Isn’t the value returned and the model whose ID is being harvested the SAME MODEL???
       */
      getTemplateObjectById: function(model) {
        return this.templateObjectIdMap[this.getTemplateObjectId(model)];
      },

      /**
       * getTemplateObjectDefaults
       * @description returns the defined defaults of a new template object
       *
       * @returns {object}  the value of this.templateObjectDefaults
       */
      getTemplateObjectDefaults: function() {
        return Mn._getValue(this.templateObjectDefaults, this);
      },

      /**
       * setupDefaultActions
       * @description Registers the “displayed” and “validate” actions with the form’s action object.
       * Actions are used in a Rule Model as the action to be performed once it's "conditions"
       * are met.
       *
       * @see {@link model/rule.model.js}
       * @see {@link model/condition.model.js}
       *
       * @returns {undefined}
       */
      setupDefaultActions: function() {
        this.registerAction('displayed', 'Show').registerAction('validate', 'Validate');
      },

      /**
       * setupDefaultOperations
       * @description Registers the “filled” operation with the form’s operations object.
       *
       * @returns {undefined}
       */
      setupDefaultOperations: function() {
        this.registerOperation('filled', 'Is Answered');
      },

      /**
       * registerIntoCollection
       * @description Will take a collection and adds a model with the passed values
       * of name and displayName.  If no displayName is passed, sets the model’s
       * displayName to name.
       *
       * @param  {object} collection  Backbone.Collection
       * @param  {string} name
       * @param  {string} displayName
       * @returns {object}  the current context of this
       */
      registerIntoCollection: function(collection, name, displayName) {
        collection.push({
          name: name,
          displayName: displayName || name,
        });
        return this;
      },

      /**
       * registerOperation
       * @description Invokes registerIntoCollection() using the operations collection
       * as an argument.
       *
       * @param  {string} name
       * @param  {string} displayName
       * @returns {function}
       */
      registerOperation: function(name, displayName) {
        return this.registerIntoCollection(this.operations, name, displayName);
      },

      /**
       * registerAction - description
       * @description Invokes registerIntoCollection() using the actions collection
       * as an argument.
       *
       * @param  {string} name
       * @param  {string} displayName
       * @returns {function}
       */
      registerAction: function(name, displayName) {
        return this.registerIntoCollection(this.actions, name, displayName);
      },

      /**
       * addConditionToConditionsMap
       * @description Will take a passed condition argument and enter it by its targetId
       * attribute to the conditionsByTargetIdMap hash
       *
       * @param  {object} condition
       * @fires Form#conditionsMap:add
       * @returns {undefined}
       */
      addConditionToConditionsMap: function(condition) {
        if (!condition.get('targetId')) {
          return;
        }

        if (this.conditionsByTargetIdMap[condition.get('targetId')]) {
          this.conditionsByTargetIdMap[condition.get('targetId')].add(condition);
        } else {
          this.conditionsByTargetIdMap[condition.get('targetId')] = new Backbone.Collection(condition);
        }

        this.trigger('conditionsMap:add:' + condition.get('targetId'));
      },

      /**
       * removeConditionFromConditionsMap
       * @description Will take a passed condition argument, find its entry in
       * the conditionsByTargetIdMap hash and remove it.
       *
       * @param  {object} condition
       * @fires Form#conditionsMap:remove
       * @returns {undefined}
       */
      removeConditionFromConditionsMap: function(condition) {
        if (!condition.get('targetId')) {
          return;
        }

        if (this.conditionsByTargetIdMap[condition.get('targetId')]) {
          this.conditionsByTargetIdMap[condition.get('targetId')].remove(condition);
        }

        this.cleanupConditionsInConditionMapByTargetId(condition.get('targetId'));

        this.trigger('conditionsMap:remove:' + condition.get('targetId'));
      },

      /**
       * modifyConditionInConditionsMap
       * @description Takes a passed condition argument, and if the previous targetId
       * is listed in the conditionsByTargetIdMap hash, will remove the previous
       * condition and delete the entry in the hash. It will then add the currently
       * passed condition to the hash.
       *
       * @param  {object} condition
       * @fires Form#conditionsMap:remove
       * @returns {undefined}
       *
       * @todo remove rendundant code and replace with invocation of removeConditionFromConditionsMap()
       */
      modifyConditionInConditionsMap: function(condition) {
        if (this.conditionsByTargetIdMap[condition.previous('targetId')]) {
          this.conditionsByTargetIdMap[condition.previous('targetId')].remove(condition);

          this.cleanupConditionsInConditionMapByTargetId(condition.previous('targetId'));

          this.trigger('conditionsMap:remove:' + condition.previous('targetId'));
        }

        this.addConditionToConditionsMap(condition);
      },

      /**
       * cleanupConditionsInConditionMapByTargetId
       * @description Looks up a condition in the conditionsByTargetIdMap hash,
       *  and if it exists and contains no conditions, will delete the hash entry.
       *
       * @param  {string} targetId
       * @returns {undefined}
       */
      cleanupConditionsInConditionMapByTargetId: function(targetId) {
        if (this.conditionsByTargetIdMap[targetId] && this.conditionsByTargetIdMap[targetId].length === 0) {
          delete this.conditionsByTargetIdMap[targetId];
        }
      },

      /**
       * getConditionCollectionTemplateObjectIsTargetedBy
       *
       * @param  {object} templateObjectModel
       * @returns {object} Backbone.Collection of conditions for the given templateObjectModel
       */
      getConditionCollectionTemplateObjectIsTargetedBy: function(templateObjectModel) {
        var conditionCollection = new Backbone.Collection();

        this.util.eachDescendent(
          templateObjectModel,
          function(child) {
            if (this.conditionsByTargetIdMap[child.id]) {
              conditionCollection.add(this.conditionsByTargetIdMap[child.id].models);
            }
          },
          this,
          { includeSelf: true }
        );

        return conditionCollection;
      },

      /**
       * isTemplateObjectTargetedByCondition
       *
       * @param  {object} templateObjectModel
       * @returns {boolean} true if templateObjectModel is a target of a condition, otherwise false
       */
      isTemplateObjectTargetedByCondition: function(templateObjectModel) {
        var conditionCollection = this.getConditionCollectionTemplateObjectIsTargetedBy(templateObjectModel);
        return !conditionCollection.isEmpty();
      },

      /**
       * doesTemplateObjectHaveRules
       *
       * @param  {object} templateObject
       * @returns {boolean|undefined}
       */
      doesTemplateObjectHaveRules: function(templateObject) {
        if (!templateObject) {
          return;
        }
        if (!templateObject.get('rules')) {
          return;
        }

        var rules = templateObject.get('rules').find(function(rule) {
          return !rule.get('hidden');
        });

        return !!(rules && rules.length !== 0);
      },

      /**
       * addChildToTemplateObjectMap
       *
       * @param  {object} child
       * @returns {undefined}
       */
      addChildToTemplateObjectMap: function(child) {
        if (!child.isNew()) {
          this.templateObjectIdMap[child.id] = child;
        }
      },

      /**
       * removeChildFromTemplateObjectMap
       *
       * @param  {object} child
       * @returns {undefined}
       */
      removeChildFromTemplateObjectMap: function(child) {
        if (!child.isNew()) {
          delete this.templateObjectIdMap[child.id];
        }
      },

      /**
       * addAllDescendentsToConditionsByTargetIdMap
       *
       * @param  {object} rootTemplateObject
       * @returns {undefined}
       */
      addAllDescendentsToConditionsByTargetIdMap: function(rootTemplateObject) {
        this.util.eachDescendent(
          rootTemplateObject,
          function(child) {
            this.util.eachRule(
              child,
              function(ruleModel) {
                this.util.eachCondition(
                  ruleModel,
                  function(condition) {
                    this.addConditionToConditionsMap(condition);
                  },
                  this
                );
              },
              this
            );
          },
          this,
          { includeSelf: true }
        );
      },

      /**
       * addAllDescendentsToTemplateObjectMap
       *
       * @param  {object} rootTemplateObject
       * @returns {undefined}
       */
      addAllDescendentsToTemplateObjectMap: function(rootTemplateObject) {
        FormObjectUtil.eachDescendent(
          rootTemplateObject,
          function(child) {
            this.addChildToTemplateObjectMap(child);
          },
          this,
          { includeSelf: true }
        );
      },

      /**
       * removeAllDescendentsFromConditionsByTargetIdMap
       *
       * @param  {object} rootTemplateObject
       * @returns {undefined}
       */
      removeAllDescendentsFromConditionsByTargetIdMap: function(rootTemplateObject) {
        this.util.eachDescendent(
          rootTemplateObject,
          function(child) {
            this.util.eachRule(
              child,
              function(ruleModel) {
                this.util.eachCondition(
                  ruleModel,
                  function(condition) {
                    this.removeConditionFromConditionsMap(condition);
                  },
                  this
                );
              },
              this
            );
          },
          this,
          { includeSelf: true }
        );
      },

      /**
       * removeAllDescendentsFromTemplateObjectMap
       *
       * @param  {object} rootTemplateObject
       * @returns {undefined}
       */
      removeAllDescendentsFromTemplateObjectMap: function(rootTemplateObject) {
        FormObjectUtil.eachDescendent(
          rootTemplateObject,
          function(child) {
            this.removeChildFromTemplateObjectMap(child);
          },
          this,
          { includeSelf: true }
        );
      },

      /**
       * bindTemplateObjectEventsToAllDescendents
       *
       * @param  {object} rootTemplateObject
       * @returns {undefined}
       */
      bindTemplateObjectEventsToAllDescendents: function(rootTemplateObject) {
        FormObjectUtil.eachDescendent(
          rootTemplateObject,
          function(child) {
            this.bindTemplateObjectEvents(child);
          },
          this,
          { includeSelf: true }
        );
      },

      /**
       * unbindTemplateObjectEventsFromAllDescendents
       *
       * @param  {object} rootTemplateObject
       * @returns {undefined}
       */
      unbindTemplateObjectEventsFromAllDescendents: function(rootTemplateObject) {
        FormObjectUtil.eachDescendent(
          rootTemplateObject,
          function(child) {
            this.unbindTemplateObjectEvents(child);
          },
          this,
          { includeSelf: true }
        );
      },

      /**
       * bindTemplateObjectEvents
       *
       * @param  {object} model
       * @returns {undefined}
       */
      bindTemplateObjectEvents: function(model) {
        this.unbindTemplateObjectEvents(model);

        _.each(
          ['begin:edit:answer', 'end:edit:answer'],
          function(eventName) {
            this.listenTo(model, eventName, function() {
              var args = _.toArray(arguments);
              this.triggerMethod.apply(this, [eventName].concat(args));
            });
          },
          this
        );

        var children = model.get('children');

        _.each(
          ['add', 'remove'],
          function(eventName) {
            this.listenTo(children, eventName, function(child) {
              var args = _.toArray(arguments);
              model.trigger.apply(model, [eventName + ':child', model].concat(args));
              children.each(function(c) {
                if (child !== c) {
                  c.trigger.apply(c, [eventName + ':sibling', c].concat(args));
                }
              });
            });
          },
          this
        );

        this.listenTo(children, 'reset', function() {
          var args = ['reset:children', model].concat(_.toArray(arguments));
          model.trigger.apply(model, args);
        });

        var ruleCollection = model.get('rules');

        this.listenTo(ruleCollection, 'add', function(ruleModel) {
          this.bindRuleEvents(model, ruleModel);
          model.trigger.apply(model, ['add:rule'].concat(_.toArray(arguments)));

          this.trigger.apply(this, ['add:rule', ruleModel].concat(_.toArray(arguments)));
        });

        if (ruleCollection) {
          ruleCollection.each(function(ruleModel) {
            this.bindRuleEvents(model, ruleModel);
          }, this);
        }

        this.listenTo(ruleCollection, 'remove', function(ruleModel, options) {
          this.stopListening(ruleModel);
          model.trigger.apply(model, ['remove:rule'].concat(_.toArray(arguments)));

          this.trigger.apply(this, ['remove:rule', ruleModel].concat(_.toArray(arguments)));
        });

        this.listenTo(ruleCollection, 'reset', function() {
          model.trigger.apply(model, ['reset:rules'].concat(_.toArray(arguments)));

          this.trigger.apply(this, ['reset:rules', ruleCollection].concat(_.toArray(arguments)));
        });

        this.listenTo(model, 'add:child', function(model, child) {
          this.triggerMethod('add:templateObject', child, { parentModel: model });
        });

        this.listenTo(model, 'remove:child', function(model, child) {
          this.triggerMethod('remove:templateObject', child, { parentModel: model });
        });

        this.listenTo(
          model,
          'change add:child remove:child reset:children add:rule remove:rule reset:rules modify:rule add:condition remove:condition reset:conditions modify:condition',
          function() {
            this.triggerMethod('modify:template', { model: model });
          }
        );
      },

      /**
       * bindRuleEvents
       *
       * @param  {object} templateObjectModel
       * @param  {object} ruleModel
       * @returns {undefined}
       */
      bindRuleEvents: function(templateObjectModel, ruleModel) {
        var conditionCollection = ruleModel.get('conditions');

        this.listenTo(ruleModel, 'change', function() {
          templateObjectModel.trigger.apply(templateObjectModel, ['modify:rule'].concat(_.toArray(arguments)));
        });
        this.listenTo(conditionCollection, 'add', function(conditionModel) {
          this.bindConditionEvents(templateObjectModel, ruleModel, conditionModel);
          templateObjectModel.trigger.apply(templateObjectModel, ['add:condition'].concat(_.toArray(arguments)));

          this.trigger.apply(
            this,
            ['add:condition', conditionModel, ruleModel, templateObjectModel].concat(_.toArray(arguments))
          );
        });

        if (conditionCollection) {
          conditionCollection.each(function(conditionModel) {
            this.bindConditionEvents(templateObjectModel, ruleModel, conditionModel);
          }, this);
        }

        this.listenTo(conditionCollection, 'remove', function(conditionModel) {
          this.stopListening(conditionModel);
          templateObjectModel.trigger.apply(templateObjectModel, ['remove:condition'].concat(_.toArray(arguments)));

          this.trigger.apply(
            this,
            ['remove:condition', conditionModel, ruleModel, templateObjectModel].concat(_.toArray(arguments))
          );
        });

        this.listenTo(conditionCollection, 'reset', function() {
          model.trigger.apply(model, ['reset:conditions'].concat(_.toArray(arguments)));

          this.trigger.apply(
            this,
            ['reset:condition', conditionCollection, ruleModel, templateObjectModel].concat(_.toArray(arguments))
          );
        });
      },

      /**
       * bindConditionEvents
       *
       * @param  {object} templateObjectModel
       * @param  {object} ruleModel
       * @param  {object} conditionModel
       * @returns {undefined}
       */
      bindConditionEvents: function(templateObjectModel, ruleModel, conditionModel) {
        this.listenTo(conditionModel, 'change', function() {
          templateObjectModel.trigger.apply(templateObjectModel, ['modify:condition'].concat(_.toArray(arguments)));

          this.trigger.apply(
            this,
            ['modify:condition', conditionModel, ruleModel, templateObjectModel].concat(_.toArray(arguments))
          );
        });

        this.listenTo(conditionModel, 'change:targetId', function() {
          templateObjectModel.trigger.apply(
            templateObjectModel,
            ['modify:condition:targetId'].concat(_.toArray(arguments))
          );

          this.trigger.apply(
            this,
            ['modify:condition:targetId', conditionModel, ruleModel, templateObjectModel].concat(_.toArray(arguments))
          );
        });
      },

      /**
       * unbindTemplateObjectEvents
       *
       * @param  {object} model
       * @returns {undefined}
       */
      unbindTemplateObjectEvents: function(model) {
        // never stop listening to templateModel
        if (model == this.templateModel) {
          return;
        }

        var children;
        if (model instanceof Backbone.Model) {
          this.stopListening(model);
          children = model.get('children');
        }
        if (children instanceof Backbone.Collection) {
          this.stopListening(children);
        }
      },

      /**
       * executeTemplateObjectAction - description
       *
       * @param  {string} actionName
       * @param  {object} model
       * @param  {object} options
       * @returns {object} current context of this
       */
      executeTemplateObjectAction: function(actionName, model, options) {
        options || (options = {});
        switch (actionName) {
          case 'remove':
            this.confirmAndDetachTemplateObject(model);
            break;
          case 'moveUp':
            this.moveTemplateObjectUp(model);
            break;
          case 'moveDown':
            this.moveTemplateObjectDown(model);
            break;
          case 'move':
            this.insertTemplateObject(options.parent, model, options.index);
            break;
        }
        return this;
      },

      /**
       * isInsertable - Checks if child can be inserted into parent at specified index
       * @param  {object} parent the parent model where to insert object
       * @param  {object} child object or object with attribute properties,
       * which will be converted to a template object
       * @param  {number} index  index where to insert child
       * @returns {boolean} true if can be inserted, false otherwise
       */
      isInsertable: function(parent, child, index) {
        // avoid inserting group inside of group
        if (this.isGroupTemplateObject(child) && this.isGroupTemplateObject(parent)) {
          return false;
        }

        if (index < 0 || index > FormObjectUtil.childCount(parent)) {
          return false;
        }

        return true;
      },

      /**
       * insertTemplateObject - Inserts a template object into the form at specified index
       * @param  {object} parent the parent model where to insert object
       * @param  {object} child object or object with attribute properties, which will be converted to a template object
       * @param  {number} index  index where to insert child
       * @returns {object} inserted object or null
       */
      insertTemplateObject: function(parent, child, index) {
        if (!this.isInsertable(parent, child, index)) {
          return null;
        }

        var hasParent = FormObjectUtil.hasParent(child),
          modelClass = null,
          options = { child: child, parent: parent, index: index };

        if (parent && parent.get('children') instanceof Backbone.Collection) {
          modelClass = parent.get('children').model;
        } else {
          modelClass = TemplateObjectModel;
        }

        if (!(child instanceof Backbone.Model)) {
          child = this.createTemplateObject(child, modelClass, options);

          // initialize content specific options
          var contentViewOptions = this.getContentViewOptions(this.getTemplateObjectTypeName(child), { silent: true });
          if (contentViewOptions && contentViewOptions.initializeNewTemplateObject) {
            contentViewOptions.initializeNewTemplateObject(this, child);
          }
        }

        // trigger before insert operation events
        if (hasParent) {
          this.triggerMethod('before:move:templateObject', child, options);
          child.trigger('before:move', child, options);
        } else {
          this.triggerMethod('before:attach:templateObject', child, options);
          child.trigger('before:attach', child, options);
        }

        // perform insert operation
        FormObjectUtil.insertChild(parent, child, index);

        // trigger insert operation events
        if (hasParent) {
          this.triggerMethod('move:templateObject', child, options);
          child.trigger('move', child, options);
        } else {
          this.triggerMethod('attach:templateObject', child, options);
          child.trigger('attach', child, options);
        }

        this.triggerMethod('modify:template', options);

        return child;
      },

      /**
       * createTemplateObject - Create template object from regular JSON object. Calling it child for consistency’s sake
       * @param {object} child regular JSON object to create template object from
       * @param {object} modelClass
       * @param {object} options
       * @returns {object}       new template object
       */
      createTemplateObject: function(child, modelClass, options) {
        child = _.extend({}, child);

        if (!modelClass) {
          modelClass = TemplateObjectModel;
        }

        if (!options) {
          options = {};
        }

        if (_.isString(child.typeName)) {
          child.type = this.findTemplateObjectTypeByName(child.typeName);
          delete child.typeName;
        }
        _.extend(child, this.getTemplateObjectDefaults());
        var typeName = this.getTemplateObjectTypeName(child);

        this.triggerMethod('before:create:templateObject', child, options);
        if (typeName) {
          this.trigger('before:create:templateObject:' + typeName, child, options);
        }
        child = new modelClass(child);
        options.model = child;
        this.triggerMethod('create:templateObject', child, options);
        if (typeName) {
          this.trigger('create:templateObject:' + typeName, child, options);
        }
        return child;
      },

      /**
       * cloneTemplateObject - Create a copy of the template object and attributes
       * Unset the ids to make sure this is a fresh object
       * @param  {object} child object to copy
       * @returns {object} copy of the object
       */
      cloneTemplateObject: function(child) {
        var newChild = child.clone();
        newChild.unset('id');
        FormObjectUtil.eachDescendent(newChild, function(child) {
          child.unset('id');
        });
        return newChild;
      },

      /**
       * confirmAndDetachTemplateObject
       *
       * @param  {object} child
       * @returns {undefined}
       */
      confirmAndDetachTemplateObject: function(child) {
        if (this.isTemplateObjectTargetedByCondition(child)) {
          var confirmationModal = this.getConfirmationModal({
            headerText: 'Confirm Remove',
            text:
              'This object is a target of one or more rule conditions. If it is removed, all associated conditions will also be removed.',
            icon: 'info circle icon',
          }).on(
            'confirm',
            function() {
              var targetedByConditions = this.getConditionCollectionTemplateObjectIsTargetedBy(child);
              targetedByConditions.each(function(condition) {
                this.util.removeCondition(condition);
              }, this);
              this.detachTemplateObject(child);
            },
            this
          );

          confirmationModal.visible = true;
        } else {
          this.detachTemplateObject(child);
        }
      },

      /**
       * detachTemplateObject - Removes template object from the form
       * @param  {object} child template object to remove
       * @returns {object} the removed object
       */
      detachTemplateObject: function(child) {
        var parent = FormObjectUtil.parent(child);
        var options = { parent: parent, model: child };
        this.triggerMethod('before:detach:templateObject', child, options);
        child.trigger('before:detach', child, options);
        FormObjectUtil.detachChild(child);
        FormObjectUtil.eachDescendent(
          child,
          function(child) {
            this.stopListening(child);
            delete this.templateObjectIdMap[child.id];
          },
          this,
          { includeSelf: true }
        );
        this.triggerMethod('detach:templateObject', child, options);
        child.trigger('detach', child, options);
        this.triggerMethod('modify:template', options);
        return child;
      },

      /**
       * moveTemplateObjectUp
       *
       * @param  {object} model
       * @returns {undefined}
       */
      moveTemplateObjectUp: function(model) {
        var parent = FormObjectUtil.parent(model),
          oldIndex = FormObjectUtil.indexOfChild(parent, model);

        this.insertTemplateObject(parent, model, Math.max(oldIndex - 1, 0));
      },

      /**
       * moveTemplateObjectDown
       *
       * @param  {object} model
       * @returns {undefined}
       */
      moveTemplateObjectDown: function(model) {
        var parent = FormObjectUtil.parent(model),
          oldIndex = FormObjectUtil.indexOfChild(parent, model),
          childCount = FormObjectUtil.childCount(parent);

        this.insertTemplateObject(parent, model, Math.min(oldIndex + 1, childCount));
      },

      /**
       * isGroupTemplateObject
       *
       * @param  {object} model
       * @returns {boolean}
       */
      isGroupTemplateObject: function(model) {
        var sections = this.getTemplateModel().get('children'),
          type = this.getTemplateObjectTypeName(model),
          isSection = sections.indexOf(model) > -1;

        return type == 'Group' && !isSection;
      },

      /**
       * isAlwaysRequired
       *
       * @param  {object} templateObjectModel
       * @returns {boolean}
       *
       * @todo hardcoding this value into FormKit can't be the best way to do this
       */
      isAlwaysRequired: function(templateObjectModel) {
        var templateObjectTypeName = this.getTemplateObjectTypeName(templateObjectModel);
        if (templateObjectTypeName === 'Principal Investigator Finder' || templateObjectTypeName === 'Research Team') {
          return true;
        }
        return false;
      },

      /**
       * isTemplateObjectTargetable
       *
       * @param  {object} templateObjectModel
       * @returns {boolean}
       */
      isTemplateObjectTargetable: function(templateObjectModel) {
        var templateObjectTypeModel = this.getTemplateObjectType(templateObjectModel);

        if (!templateObjectTypeModel) {
          return false;
        } else if (!templateObjectTypeModel.get) {
          return templateObjectTypeModel.isTargetable;
        } else {
          return templateObjectTypeModel.get('isTargetable');
        }
      },

      /**
       * getTemplateObjectCount
       *
       * @returns {number}
       */
      getTemplateObjectCount: function() {
        var count = 0;

        FormObjectUtil.eachDescendent(this.getTemplateModel(), function() {
          count++;
        });

        return count;
      },

      /**
       * getAnswerValueModel
       *
       * @param  {object} model
       * @param  {number} index
       * @returns {object} the answerValueModel at position index in an answerValueCollection for a given template object
       */
      getAnswerValueModel: function(model, index) {
        return FormObjectUtil.getAnswerValueModel.apply(this, arguments);
      },

      /**
       * getAnswerValueCollection
       *
       * @param  {object} templateObject
       * @returns {object} the answerValueCollection for a given templateObject
       */
      getAnswerValueCollection: function(templateObject) {
        return FormObjectUtil.getAnswerValueCollection.apply(this, arguments);
      },

      /**
       * getAnswerModel
       *
       * @param  {object} templateObject
       * @returns {object} the answerValueModel for a given templateObject
       */
      getAnswerModel: function(templateObject) {
        return FormObjectUtil.getAnswerModel.apply(this, arguments);
      },

      /**
       * getContentViewConfiguration
       *
       * @param  {string} typeName
       * @param  {object} options
       * @returns {object} the content view configuration for a given template object type
       */
      getContentViewConfiguration: function(typeName, options) {
        var channel = Radio.channel(this.contentChannelName),
          name = _.isString(typeName) ? typeName.replace(/\s/g, '_') : null;

        var contentViewConfiguration = channel.request(name);

        if (!contentViewConfiguration) {
          if (!options || !options.silent) {
            var message = 'Template object type ' + '"' + typeName + '" has no associated form object content view.';
            Promise.reject(new Error(message));
          }
          contentViewConfiguration = { viewClass: BaseContentView.extend({ template: false }), options: {} };
        }

        return contentViewConfiguration;
      },

      /**
       * getContentView
       *
       * @param  {string} typeName
       * @param  {object} options
       * @returns {object} the constructor class for a given templateTypeObject's typeName
       */
      getContentView: function(typeName, options) {
        return this.getContentViewConfiguration(typeName, options).viewClass;
      },

      /**
       * getContentViewOption
       *
       * @param  {string} typeName
       * @param  {string} optionName
       * @returns {object}
       */
      getContentViewOption: function(typeName, optionName) {
        var contentViewOptions = this.getContentViewConfiguration(typeName).options;
        return contentViewOptions[optionName];
      },

      /**
       * getContentViewOptions
       *
       * @param  {string} typeName
       * @param  {object} options
       * @returns {object} the content view options object for a given template object type
       */
      getContentViewOptions: function(typeName, options) {
        return this.getContentViewConfiguration(typeName, options).options;
      },

      /**
       * triggerBeginEditAnswerEvent
       *
       * @param  {object} answerView
       * @param  {object} templateObjectModel
       * @param  {object} options
       * @returns {undefined}
       *
       * @todo confirm the assumption that this returns undefined because the invoked function returns undefined
       */
      triggerBeginEditAnswerEvent: function(answerView, templateObjectModel, options) {
        return this.util.triggerBeginEditAnswerEvent(answerView, templateObjectModel, this, options);
      },

      /**
       * triggerEndEditAnswerEvent
       *
       * @param  {object} answerView
       * @param  {object} templateObjectModel
       * @param  {object} options
       * @returns {undefined}
       *
       * @todo confirm the assumption that this returns undefined because the invoked function returns undefined
       */
      triggerEndEditAnswerEvent: function(answerView, templateObjectModel, options) {
        return this.util.triggerEndEditAnswerEvent(answerView, templateObjectModel, this, options);
      },

      /**
       * getFormObjectState
       *
       * @param  {string} templateObjectId
       * @returns {object|undefined}
       */
      getFormObjectState: function(templateObjectId) {
        var formObjectView = this.getFormObjectView(templateObjectId);
        if (formObjectView) {
          return formObjectView.getFormObjectState();
        }
      },

      /**
       * whenFormObjectStateAvailable
       *
       * @param  {string} templateObjectId
       * @param  {function} callback
       * @param  {object} context
       * @returns {object}
       */
      whenFormObjectStateAvailable: function(templateObjectId, callback, context) {
        return this.whenFormObjectViewAvailable(
          templateObjectId,
          function() {
            callback.call(context || this, this.getFormObjectState(templateObjectId), templateObjectId);
          },
          this
        );
      },

      /**
       * registerFormObjectView
       *
       * @param  {string} templateObjectId
       * @param  {object} formObjectView
       * @returns {object} the context of this
       */
      registerFormObjectView: function(templateObjectId, formObjectView) {
        if (!this._formObjectViewRegistry) {
          this._formObjectViewRegistry = {};
        }
        this._formObjectViewRegistry[templateObjectId] = formObjectView;
        formObjectView.once(
          'destroy',
          function() {
            this.deregisterFormObjectView(templateObjectId);
          },
          this
        );
        this.triggerMethod('register:formObjectView', this, formObjectView, templateObjectId);
        this.triggerMethod('register:formObjectView:' + templateObjectId, this, formObjectView, templateObjectId);
        return this;
      },

      /**
       * deregisterFormObjectView
       *
       * @param  {string} templateObjectId description
       * @returns {object} the context of this
       */
      deregisterFormObjectView: function(templateObjectId) {
        var formObjectView = this.getFormObjectView(templateObjectId);
        if (formObjectView) {
          delete this._formObjectViewRegistry[templateObjectId];
          this.triggerMethod('deregister:formObjectView', this, formObjectView, templateObjectId);
          this.triggerMethod('deregister:formObjectView:' + templateObjectId, this, formObjectView, templateObjectId);
        }
        return this;
      },

      /**
       * getFormObjectView
       *
       * @param  {string} templateObjectId
       * @returns {object} formObjectView
       */
      getFormObjectView: function(templateObjectId) {
        if (this._formObjectViewRegistry && this._formObjectViewRegistry[templateObjectId]) {
          return this._formObjectViewRegistry[templateObjectId];
        }
      },

      /**
       * whenFormObjectViewAvailable
       *
       * @param  {string} templateObjectId
       * @param  {function} callback
       * @param  {object} context
       * @returns {object}
       */
      whenFormObjectViewAvailable: function(templateObjectId, callback, context) {
        if (!_.isFunction(callback)) {
          return this;
        }
        function invokeCallback() {
          callback.call(context || this, this.getFormObjectView(templateObjectId), templateObjectId);
        }
        if (this.getFormObjectView(templateObjectId)) {
          invokeCallback.call(this);
        } else {
          this.once('register:formObjectView:' + templateObjectId, invokeCallback, this);
        }
        return this;
      },

      /**
       * whenFormObjectViewInDom
       *
       * @param  {string} templateObjectId
       * @param  {function} callback
       * @param  {object} context
       * @param  {number} maxRetries
       * @returns {undefined}
       *
       * @todo This function needs clean up and refactoring. Also, investigation
       * as to where and why this functionality is needed.
       */
      whenFormObjectViewInDom: function(templateObjectId, callback, context, maxRetries) {
        maxRetries = maxRetries || 100;
        var retryCount = 0;
        this.whenFormObjectViewAvailable(
          templateObjectId,
          function(formObjectView) {
            function runCheck() {
              if (jQuery.contains(document.body, formObjectView.el)) {
                if (formObjectView.isRendered) {
                  callback.call(context || this, formObjectView, templateObjectId);
                } else {
                  formObjectView.once(
                    'render',
                    function() {
                      callback.call(context || this, formObjectView, templateObjectId);
                    },
                    this
                  );
                }
              } else if (retryCount < maxRetries) {
                retryCount++;
                // poll until the element is in the dom
                setTimeout(runCheck, 50);
              }
            }
            runCheck();
          },
          this
        );
      },

      /**
       * getSectionModelForTemplateObjectModel
       *
       * @param  {object} templateObjectModel
       * @returns {model}
       */
      getSectionModelForTemplateObjectModel: function(templateObjectModel) {
        var sectionModel = templateObjectModel;
        while (this.getTemplateModel() != this.util.parent(sectionModel) && this.util.parent(sectionModel)) {
          sectionModel = this.util.parent(sectionModel);
        }
        return sectionModel;
      },

      /**
       * activateAndScrollToTemplateObject
       *
       * @param  {string} templateObjectId
       * @param  {object} container
       * @param  {object} options
       * @returns {undefined}
       */
      activateAndScrollToTemplateObject: function(templateObjectId, container, options) {
        var templateObjectModel = this.getTemplateObjectById(templateObjectId);

        if (!templateObjectModel) {
          return;
        }

        var sectionModel = this.getSectionModelForTemplateObjectModel(templateObjectModel);

        if (sectionModel) {
          this.setActiveSectionModel(sectionModel);
        }

        if (arguments.length < 3) {
          options = container;
          container = $('html,body');
        }

        this.whenFormObjectViewInDom(templateObjectId, function(formObjectView) {
          jQuery(container).animate(
            {
              scrollTop: formObjectView.$el.offset().top,
            },
            options
          );
        });
      },
    },
    {
      /**
       * registerContentView
       *
       * @param  {string} name
       * @param  {object} ContentView
       * @param  {object} options
       * @returns {object}
       */
      registerContentView: function(name, ContentView, options) {
        var channel = Radio.channel(Form.prototype.contentChannelName),
          replyName = name.replace(/\s/g, '_');

        channel.reply(replyName, { viewClass: ContentView, options: options });

        return this;
      },

      /**
       * registerModelRelationships
       *
       * @param  {string} modelName
       * @param  {object} Relationships
       * @param  {object} options
       * @returns {object}
       */
      registerModelRelationships: function(modelName, Relationships, options) {
        var channel = Radio.channel(Form.prototype.modelRelationshipChannelName);
        var ExistingRelationships = channel.request(modelName);

        channel.reply(modelName, _.extend({}, ExistingRelationships, Relationships));

        return this;
      },

      /**
       * getModelRelationships
       *
       * @param  {string} modelName
       * @param  {object} options
       * @returns {object}
       */
      getModelRelationships: function(modelName, options) {
        var channel = Radio.channel(Form.prototype.modelRelationshipChannelName);

        return channel.request(modelName);
      },
    }
  );

  var defaultOptions = {};

  var selectableOptions = {
    childTemplateObject: { text: 'Item', typeName: 'Item' },
    initializeNewTemplateObject: function(form, templateObjectModel) {
      form.insertTemplateObject(templateObjectModel, selectableOptions.childTemplateObject);
    },
  };

  var tableOptions = {
    childTemplateObject: { text: 'Column', typeName: 'Text Input' },
    initializeNewTemplateObject: function(form, templateObjectModel) {
      form.insertTemplateObject(templateObjectModel, tableOptions.childTemplateObject);
    },
  };

  //prettier-ignore
  Form.registerContentView('Information', InformationContentView, defaultOptions)
      .registerContentView('Text Input', InputContentView, defaultOptions)
      .registerContentView('Radio Button', RadioContentView, selectableOptions)
      .registerContentView('Check Box', CheckboxContentView, selectableOptions)
      .registerContentView('Dropdown', DropdownContentView, selectableOptions)
      .registerContentView('Date Input', DateInputContentView, defaultOptions)
      .registerContentView('Numeric Input', NumericInputContentView, defaultOptions)
      .registerContentView('Currency Input', CurrencyInputContentView, defaultOptions)
      .registerContentView('Table', TableContentView, tableOptions)
      .registerContentView('Date & Time Input', DateTimeContentView, defaultOptions);

  module.exports = Form;
});

